import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  className = '',
}) => {
  if (totalPages <= 1) {
    return (
      <div className={`flex items-center justify-between text-sm text-trident-gray-500 mt-4 ${className}`}>
        <div>
          Showing <span className="font-semibold text-trident-gray-700">{totalItems}</span>{' '}
          {totalItems === 1 ? 'entry' : 'entries'}
        </div>
      </div>
    );
  }

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Build a condensed page list: [1, ..., current-1, current, current+1, ..., last]
  const pages: (number | string)[] = [];
  const maxButtons = 7;
  if (totalPages <= maxButtons) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push('…');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('…');
    pages.push(totalPages);
  }

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 ${className}`}>
      <div className="text-sm text-trident-gray-500">
        Showing <span className="font-semibold text-trident-gray-700">{startItem}</span> to{' '}
        <span className="font-semibold text-trident-gray-700">{endItem}</span> of{' '}
        <span className="font-semibold text-trident-gray-700">{totalItems}</span> entries
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1.5 rounded-lg border border-trident-gray-200 bg-white text-trident-gray-600 font-display font-semibold text-sm hover:bg-trident-gray-50 hover:border-trident-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-trident-gray-200 transition-colors"
          aria-label="Previous page"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="inline-block"><polyline points="15 18 9 12 15 6"/></svg>
        </button>

        {pages.map((p, i) =>
          typeof p === 'string' ? (
            <span key={`ellipsis-${i}`} className="px-2 text-trident-gray-400 text-sm">{p}</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`min-w-[36px] h-9 px-3 rounded-lg font-display font-semibold text-sm transition-all ${
                p === currentPage
                  ? 'bg-trident-gradient text-white shadow-trident'
                  : 'border border-trident-gray-200 bg-white text-trident-gray-600 hover:bg-trident-gray-50 hover:border-trident-orange/40 hover:text-trident-orange'
              }`}
            >
              {p}
            </button>
          )
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1.5 rounded-lg border border-trident-gray-200 bg-white text-trident-gray-600 font-display font-semibold text-sm hover:bg-trident-gray-50 hover:border-trident-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-trident-gray-200 transition-colors"
          aria-label="Next page"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="inline-block"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>
  );
};

export default Pagination;
